import React from 'react';
import { History, Trash2, Layers, Heart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для отображения истории циклов
 */
const CycleHistorySidebar = ({ 
  savedCycles, 
  deleteSavedCycle, 
  loadSavedCycle 
}) => {
  // Если нет сохраненных циклов
  if (!savedCycles || savedCycles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <History className="w-5 h-5 mr-2 text-rose-500" />
          История циклов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные данные о ваших циклах. После расчета вы можете сохранить текущий цикл, чтобы отслеживать статистику.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <History className="w-5 h-5 mr-2 text-rose-500" />
          История циклов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCycles.length} {getRecordCountText(savedCycles.length)}
        </span>
      </div>

      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
        {savedCycles.sort((a, b) => new Date(b.date) - new Date(a.date)).map((cycle) => (
          <div 
            key={cycle.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {cycle.notes || `Цикл ${formatDate(cycle.formData.lastPeriodDate, 'short')}`}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCycle(cycle.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCycle(cycle.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Последняя менструация:</span>
                <span className="font-medium">
                  {formatDate(cycle.formData.lastPeriodDate, 'short')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Длительность цикла:</span>
                <span>
                  {cycle.formData.cycleLength} дней
                </span>
              </div>
              <div className="flex justify-between">
                <span>Овуляция:</span>
                <span className="font-semibold text-rose-600 dark:text-rose-400 flex items-center">
                  <Heart className="w-3 h-3 mr-1" />
                  {formatDate(cycle.results.ovulationDate, 'short')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Блок со статистикой, если есть достаточное количество циклов */}
      {savedCycles.length >= 3 && (
        <div className="mt-4 bg-rose-50 dark:bg-rose-900/20 p-3 rounded-lg border border-rose-100 dark:border-rose-800">
          <h4 className="text-sm font-medium text-rose-700 dark:text-rose-300 mb-2 flex items-center">
            <Heart className="w-4 h-4 mr-1" />
            Статистика циклов
          </h4>
          <StatisticsSummary savedCycles={savedCycles} />
        </div>
      )}
    </div>
  );
};

/**
 * Компонент для отображения статистики по сохраненным циклам
 */
const StatisticsSummary = ({ savedCycles }) => {
  // Рассчитываем среднюю длительность цикла
  const averageCycleLength = savedCycles.reduce((sum, cycle) => {
    return sum + parseInt(cycle.formData.cycleLength, 10);
  }, 0) / savedCycles.length;

  // Рассчитываем среднюю длительность менструации
  const averagePeriodLength = savedCycles.reduce((sum, cycle) => {
    return sum + parseInt(cycle.formData.periodLength, 10);
  }, 0) / savedCycles.length;

  // Рассчитываем регулярность цикла
  const cycleRegularity = calculateCycleRegularity(savedCycles);

  return (
    <div className="space-y-2 text-xs">
      <div className="flex justify-between">
        <span className="text-rose-600 dark:text-rose-400">Средний цикл:</span>
        <span className="font-medium text-rose-700 dark:text-rose-300">{averageCycleLength.toFixed(1)} дней</span>
      </div>
      <div className="flex justify-between">
        <span className="text-rose-600 dark:text-rose-400">Средняя менструация:</span>
        <span className="font-medium text-rose-700 dark:text-rose-300">{averagePeriodLength.toFixed(1)} дней</span>
      </div>
      <div className="flex justify-between">
        <span className="text-rose-600 dark:text-rose-400">Регулярность цикла:</span>
        <span className="font-medium text-rose-700 dark:text-rose-300">{cycleRegularity}</span>
      </div>
    </div>
  );
};

/**
 * Рассчитывает регулярность цикла на основе разброса длительностей
 */
const calculateCycleRegularity = (savedCycles) => {
  // Получаем все длительности циклов
  const cycleLengths = savedCycles.map(cycle => parseInt(cycle.formData.cycleLength, 10));
  
  // Рассчитываем стандартное отклонение
  const mean = cycleLengths.reduce((sum, length) => sum + length, 0) / cycleLengths.length;
  const variance = cycleLengths.reduce((sum, length) => sum + Math.pow(length - mean, 2), 0) / cycleLengths.length;
  const stdDev = Math.sqrt(variance);
  
  // Определяем регулярность
  if (stdDev < 1) {
    return "Очень регулярный";
  } else if (stdDev < 3) {
    return "Регулярный";
  } else if (stdDev < 5) {
    return "Умеренно регулярный";
  } else {
    return "Нерегулярный";
  }
};

/**
 * Возвращает правильное склонение слова "запись" в зависимости от числа
 */
const getRecordCountText = (count) => {
  if (count % 10 === 1 && count % 100 !== 11) {
    return 'запись';
  } else if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) {
    return 'записи';
  } else {
    return 'записей';
  }
};

export default CycleHistorySidebar;